import { skipToken, useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { AlertCircleIcon, Loader } from 'lucide-react'
import { Suspense, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useRBAC, useSuspenseQuery } from '@/lib/hooks'
import { RoleID } from '@/lib/rbac'
import { TransferOwnershipFallback } from '@/routes/user-management/transfer-ownership-sheet/fallback'
import type {
  OwnershipCompanyUsersQuery,
  OwnershipCompanyUsersQueryVariables,
  TransferCompanyOwnershipMutation,
  TransferCompanyOwnershipMutationVariables,
} from '@/routes/user-management/transfer-ownership-sheet/index.types.gen'

const schema = z.object({
  password: z.string().min(1, 'Password is required'),
  idNewOwner: z.string().min(1, 'New owner is required'),
})

const COMPANY_USERS_QUERY = gql`
  query OwnershipCompanyUsers($idCompany: String!) {
    user {
      id
    }
    company(id: $idCompany) {
      id
      name
      users {
        id
        user {
          id
          image
          fullName
        }
        capabilities {
          id
          idRole
        }
      }
    }
  }
`

const TRANSFER_COMPANY_OWNERSHIP = gql`
  mutation TransferCompanyOwnership(
    $password: String!
    $idCompany: String!
    $idNewOwner: String!
  ) {
    transferCompanyOwnership(
      password: $password
      idCompany: $idCompany
      idNewOwner: $idNewOwner
    )
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const TransferOwnershipSheetContent = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()

  const [transferOwnership] = useMutation<
    TransferCompanyOwnershipMutation,
    TransferCompanyOwnershipMutationVariables
  >(TRANSFER_COMPANY_OWNERSHIP)

  const { data } = useSuspenseQuery<
    OwnershipCompanyUsersQuery,
    OwnershipCompanyUsersQueryVariables
  >(COMPANY_USERS_QUERY, isOpen ? { variables: { idCompany } } : skipToken)
  const company = data?.company
  const users = (company?.users || []).filter((user) => {
    return user.capabilities.some((c) => c.idRole === RoleID.COMPANY_ADMIN)
  })
  const loggedInUser = data?.user
  const owner = (company?.users || []).find((user) =>
    user.capabilities.some((c) => c.idRole === RoleID.COMPANY_OWNER)
  )

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    values: {
      idNewOwner: loggedInUser?.id || '',
      password: '',
    },
  })

  const {
    handleSubmit,
    formState: { isValid },
  } = form

  const onSubmit = async (values: z.infer<typeof schema>) => {
    if (isPending || !isValid) {
      return
    }

    startTransition(async () => {
      try {
        await transferOwnership({
          variables: {
            idCompany,
            idNewOwner: values.idNewOwner,
            password: values.password,
          },
        })
        toast(
          <ToasterContent variant="success">
            Ownership transferred successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to transfer ownership
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <>
      <Alert variant="destructive" className="w-[calc(100%-2rem)] mx-4 mb-4">
        <AlertCircleIcon />
        <AlertDescription>
          <p>
            You are going to transfer ownership of <b>{company?.name}</b> to
            another user, and you will be given administrator access. Owners and
            admins have basically the same full access over a company but the
            owner’s access cannot be modified by any other user.
          </p>
        </AlertDescription>
      </Alert>
      <div className="px-4 overflow-y-auto pb-2">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormItem>
              <FormLabel>Current company owner</FormLabel>
              <div className="flex items-center gap-2 text-sm">
                <Avatar className="size-4">
                  <AvatarImage
                    src={owner?.user?.image ?? ''}
                    title={owner?.user?.fullName ?? ''}
                  />
                </Avatar>
                {owner?.user?.fullName}
              </div>
            </FormItem>
            <FormField
              control={form.control}
              name="idNewOwner"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New company owner</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a verified email to display" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem value={user.user.id} key={user.user.id}>
                          <Avatar className="size-4">
                            <AvatarImage
                              src={user.user.image ?? ''}
                              title={user.user.fullName ?? ''}
                            />
                          </Avatar>
                          {user.user.fullName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Enter your password"
                      aria-label="Password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>
      <SheetFooter className="sticky">
        <Button
          type="submit"
          disabled={isPending}
          onClick={handleSubmit(onSubmit)}
        >
          {isPending ? <Loader className="animate-spin" /> : null}
          Transfer ownership
        </Button>
      </SheetFooter>
    </>
  )
}

export const TransferOwnershipSheet = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()

  if (!rbac.company(idCompany).users.ownership.update.can()) {
    return null
  }

  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Transfer ownership</SheetTitle>
        </SheetHeader>
        <Suspense fallback={<TransferOwnershipFallback />}>
          <TransferOwnershipSheetContent isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </SheetContent>
    </Sheet>
  )
}
